import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  List, 
  ListItem, 
  ListItemText, 
  Divider, 
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase-config';
import { getAuth } from 'firebase/auth';

const ManageQA = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [commonQuestions, setCommonQuestions] = useState([]);
  
  // State for adding new Q&A
  const [newQuestion, setNewQuestion] = useState('');
  const [newAnswer, setNewAnswer] = useState('');
  
  // State for editing existing Q&A
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(-1);
  const [editQuestion, setEditQuestion] = useState('');
  const [editAnswer, setEditAnswer] = useState('');
  
  const auth = getAuth();
  const userId = auth.currentUser?.uid;
  
  // Load user data
  useEffect(() => {
    const loadData = async () => {
      if (!userId) return;
      
      try {
        setLoading(true);
        const businessRef = doc(db, 'businesses', userId);
        const businessDoc = await getDoc(businessRef);
        
        if (businessDoc.exists() && businessDoc.data().commonQuestions) {
          setCommonQuestions(businessDoc.data().commonQuestions);
        }
      } catch (err) {
        console.error('Error loading Q&A data:', err);
        setError('Failed to load Q&A data');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [userId]);
  
  const handleAddQuestion = () => {
    if (!newQuestion.trim() || !newAnswer.trim()) {
      setError('Both question and answer are required');
      return;
    }
    
    setCommonQuestions([
      ...commonQuestions,
      { question: newQuestion.trim(), answer: newAnswer.trim() }
    ]);
    
    setNewQuestion('');
    setNewAnswer('');
    setError('');
  };
  
  const handleDeleteQuestion = (index) => {
    const updatedQuestions = [...commonQuestions];
    updatedQuestions.splice(index, 1);
    setCommonQuestions(updatedQuestions);
  };
  
  const handleEditStart = (index) => {
    setEditIndex(index);
    setEditQuestion(commonQuestions[index].question);
    setEditAnswer(commonQuestions[index].answer);
    setEditDialogOpen(true);
  };
  
  const handleEditSave = () => {
    if (!editQuestion.trim() || !editAnswer.trim()) {
      return;
    }
    
    const updatedQuestions = [...commonQuestions];
    updatedQuestions[editIndex] = {
      question: editQuestion.trim(),
      answer: editAnswer.trim()
    };
    
    setCommonQuestions(updatedQuestions);
    setEditDialogOpen(false);
  };
  
  const handleSave = async () => {
    if (!userId) {
      setError('You must be logged in to save');
      return;
    }
    
    try {
      setLoading(true);
      const businessRef = doc(db, 'businesses', userId);
      
      await setDoc(businessRef, {
        commonQuestions,
        updatedAt: new Date().toISOString()
      }, { merge: true });
      
      setSuccess(true);
      
      // Navigate to dashboard after a short delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (err) {
      console.error('Error saving Q&A data:', err);
      setError('Failed to save Q&A data');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/dashboard')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5">Manage Q&A</Typography>
      </Box>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>Q&A saved successfully!</Alert>}
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="body1" paragraph>
          Add, edit, or delete common questions and answers that your AI assistant will use when responding to customers.
        </Typography>
        
        <Typography variant="h6" gutterBottom>
          Current Q&A ({commonQuestions.length})
        </Typography>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress />
          </Box>
        ) : (
          <List>
            {commonQuestions.map((qa, index) => (
              <React.Fragment key={index}>
                <ListItem>
                  <ListItemText
                    primary={qa.question}
                    secondary={qa.answer}
                  />
                  <IconButton edge="end" onClick={() => handleEditStart(index)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton edge="end" onClick={() => handleDeleteQuestion(index)}>
                    <DeleteIcon />
                  </IconButton>
                </ListItem>
                {index < commonQuestions.length - 1 && <Divider />}
              </React.Fragment>
            ))}
            
            {commonQuestions.length === 0 && (
              <Typography variant="body2" sx={{ p: 2, color: 'text.secondary' }}>
                No questions added yet. Add your first Q&A below.
              </Typography>
            )}
          </List>
        )}
      </Paper>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Add New Q&A
        </Typography>
        
        <TextField
          fullWidth
          label="Question"
          value={newQuestion}
          onChange={(e) => setNewQuestion(e.target.value)}
          placeholder="e.g., What are your business hours?"
          margin="normal"
        />
        
        <TextField
          fullWidth
          label="Answer"
          value={newAnswer}
          onChange={(e) => setNewAnswer(e.target.value)}
          multiline
          rows={3}
          placeholder="e.g., We are open Monday-Friday from 9am to 5pm."
          margin="normal"
        />
        
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddQuestion}
          disabled={!newQuestion.trim() || !newAnswer.trim() || loading}
          sx={{ mt: 2 }}
        >
          Add Question
        </Button>
      </Paper>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/dashboard')}
        >
          Back to Dashboard
        </Button>
        
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Save All Changes'}
        </Button>
      </Box>
      
      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Edit Question & Answer</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Question"
            value={editQuestion}
            onChange={(e) => setEditQuestion(e.target.value)}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Answer"
            value={editAnswer}
            onChange={(e) => setEditAnswer(e.target.value)}
            multiline
            rows={3}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSave} color="primary">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageQA; 